import React from "react";
import "./LoadingSpinner.css";

const LoadingSpinner = ({
  size = "medium",
  message = "Loading...",
  overlay = false,
  color = "primary",
  className = "",
  ariaLabel = "Loading content",
}) => {
  const sizeClasses = {
    small: "spinner-small",
    medium: "spinner-medium",
    large: "spinner-large",
  };

  const colorClasses = {
    primary: "spinner-primary",
    secondary: "spinner-secondary",
    white: "spinner-white",
  };

  const spinnerClasses = [
    "loading-spinner",
    sizeClasses[size] || sizeClasses.medium,
    colorClasses[color] || colorClasses.primary,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <div className={spinnerClasses} role="status" aria-label={ariaLabel}>
      <div className="spinner-circle">
        <div className="spinner-path"></div>
      </div>
      {message && (
        <span className="spinner-message" aria-live="polite">
          {message}
        </span>
      )}
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );

  if (overlay) {
    return (
      <div
        className="loading-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Loading"
      >
        {content}
      </div>
    );
  }

  return content;
};

// Skeleton loader component for better perceived performance
export const SkeletonLoader = ({
  width = "100%",
  height = "20px",
  variant = "text",
  animation = "pulse",
  className = "",
}) => {
  const skeletonClasses = [
    "skeleton-loader",
    `skeleton-${variant}`,
    `skeleton-animation-${animation}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const style = {
    width,
    height: variant === "text" ? "1em" : height,
  };

  return <div className={skeletonClasses} style={style} aria-hidden="true" />;
};

// Progress bar component
export const ProgressBar = ({
  progress = 0,
  showPercentage = true,
  color = "primary",
  height = "8px",
  className = "",
  ariaLabel = "Progress",
}) => {
  const progressClasses = ["progress-bar", `progress-${color}`, className]
    .filter(Boolean)
    .join(" ");

  const clampedProgress = Math.max(0, Math.min(100, progress));

  return (
    <div className={progressClasses} style={{ height }}>
      <div
        className="progress-bar-container"
        role="progressbar"
        aria-valuenow={clampedProgress}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label={ariaLabel}
      >
        <div
          className="progress-bar-fill"
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
      {showPercentage && (
        <span className="progress-percentage" aria-live="polite">
          {Math.round(clampedProgress)}%
        </span>
      )}
    </div>
  );
};

export default LoadingSpinner;
